import { styled } from '@mui/material';
import classNames from 'classnames';

import { AircraftData, AirMetar } from '#entities';
import { AIRCRAFT_SVG_SIZE, AircraftIcon, type Point } from '#shared';

import { EZLayers, ZLayer } from '../../data-context';
import { useGeoMap } from '../../GeoMap';
import { isGeoPointVisible } from '../../helpers';
import { useSelectHighlightedAirports } from '../../widgetManager';
import { airportMark, metarMark } from '../lib';
import { useOverlay } from '../model';

import { AviationAirportAircrafts } from './AviationAirportAircrafts';

export interface AviationAirportProps {
  id: number;
  longitude: number;
  latitude: number;
  label: string;
  metar: AirMetar | null;
  aircraftTailNumbers: string[];
  aircraftsByTailNumbers: Record<string, AircraftData>;
  ICAO: string | null;
}
const PREFIX = 'TSMED-AviationAirport';
const aviationAirportClasses = {
  airportWrapper: `${PREFIX}-airportWrapper`,
  airportHighlight: `${PREFIX}-airportHighlight`,
  airportMark: `${PREFIX}-airportMark`,
  airportMarkFlightCategory: `${PREFIX}-airportMarkFlightCategory`,

  airportLabel: `${PREFIX}-airportLabel`,
  airportAircraftsWrapper: `${PREFIX}-airportAircraftsWrapper`,
  airportAircraftsMark: `${PREFIX}-airportAircraftsMark`,
  aircraftWrapper: `${PREFIX}-aircraftWrapper`,
  aircraft: `${PREFIX}-aircraft`,
};

const Root = styled('g')(({ theme: { palette, mixins, utils } }) => ({
  ...mixins.flightCategoryStyles(palette, { prefix: '&' }),
  color: `var(--primaryColor, ${palette.grey[700]})`,

  '&:focus': {
    outline: 'none',
  },
  [`.${aviationAirportClasses.airportHighlight}`]: {
    display: 'none',
    visibility: 'hidden',
    transition: 'opacity 0.2s ease-in-out',
    pointerEvents: 'none',
    ['&.highlighted']: {
      display: 'block',
      visibility: 'visible',
    },
  },
  ['.suppressed']: {
    opacity: 0.3,
  },
  [`.${aviationAirportClasses.airportMark}`]: {
    fill: `var(--primaryColor, ${palette.grey[700]})`,
  },

  [`&.${aviationAirportClasses.airportMarkFlightCategory}`]: {
    fill: 'currentColor',

    filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5))',
  },

  [`.${aviationAirportClasses.airportLabel}`]: {
    fill: 'currentColor',
    // color: palette.grey[200],
    fontSize: utils.clamp(10, 14, 'sm-xl'),
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 500,
    textAnchor: 'start',
    visibility: 'hidden',
    opacity: 0,
    textShadow:
      '1px 0px 0px rgba(0, 0, 0, 1), -1px 0px 0px rgba(0, 0, 0, 1), 0px 1px 0px rgba(0, 0, 0, 1),0px -1px 0px rgba(0, 0, 0, 1)',
    transition: 'visibility 250ms ease-in-out,opacity 250ms ease-in-out',
    pointerEvents: 'none',
  },
  [`&:hover, &.highlighted`]: {
    zIndex: 1,
    [`.${aviationAirportClasses.airportLabel}`]: {
      visibility: 'visible',
      opacity: 1,
      textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
      strokeWidth: 4,
      stroke: 'black',
      paintOrder: 'stroke',
    },
  },
  [`.${aviationAirportClasses.airportAircraftsMark}`]: {
    textShadow: '0px 0px 3px rgba(0, 0, 0, 1)',
    color: `var(--primaryColor, ${palette.grey[200]})`,
    fill: 'currentColor',
    cursor: 'pointer',
    transition: 'all 250ms ease-in-out',
    fontSize: utils.clamp(10, 14, 'sm-xl'),
    fontFamily: '"JetBrains Mono", monospace',
    fontWeight: 500,

    ['&:hover']: {
      strokeWidth: 4,
      stroke: 'black',
      paintOrder: 'stroke',
    },
  },
  [`.${aviationAirportClasses.airportAircraftsWrapper}`]: {
    transform: 'translate(12px, 24px)',
    textAnchor: 'left',

    [`.${aviationAirportClasses.airportAircraftsMark}`]: {},
  },

  [`.${aviationAirportClasses.aircraftWrapper}`]: {
    transformOrigin: 'center',
    color: palette.grey[200],
    [`.${aviationAirportClasses.aircraft}`]: {
      fill: 'currentColor',
      path: {
        fill: 'inherit',
        transform: 'rotate(var(--heading, 0deg))',
        transformOrigin: 'center',
      },
    },
  },
}));

export const AviationAirport = ({
  longitude,
  latitude,
  metar,
  aircraftTailNumbers,
  aircraftsByTailNumbers: aircraftsByTailNumbers,
  label,
  id,
}: AviationAirportProps) => {
  const { projectionRef } = useGeoMap();
  const { isInRotation } = useOverlay();
  const highlightedAirports = useSelectHighlightedAirports();

  const coordinates: Point = [longitude, latitude];

  // Hide airports on the far side of the globe — d3's orthographic projection
  // still returns screen coordinates for back-facing points (TSMED-1407).
  if (!isGeoPointVisible(projectionRef.current, coordinates)) {
    return null;
  }

  const [x, y] = projectionRef.current(coordinates) as Point;

  const isHighlighted = highlightedAirports.includes(id.toString() ?? '');
  const isSuppressed =
    !!highlightedAirports.length && !isHighlighted && !isInRotation.current;
  return (
    <Root
      className={classNames(
        'AviationAirport',
        aviationAirportClasses.airportMarkFlightCategory,
        `flight-category-${metar?.flightCategory ?? 'NA'}`,
        {
          suppressed: isSuppressed,
          ['highlighted']: isHighlighted,
        },
      )}
      key={id}
      transform={`translate(${x}, ${y})`}
    >
      <g>
        <circle
          className={classNames(aviationAirportClasses.airportHighlight, {
            ['highlighted']: isHighlighted,
          })}
          cx="0"
          cy="0"
          r="24"
          fill={`url('#airport-pulse-gradient-${metar?.flightCategory ?? 'NA'}')`}
        />
        <path
          className={classNames(aviationAirportClasses.airportMark, {
            ['suppressed']: isSuppressed,
          })}
          d={airportMark}
        />
        <path
          className={classNames({
            ['suppressed']: isSuppressed,
          })}
          d={metarMark}
        />
        <text
          className={classNames(aviationAirportClasses.airportLabel)}
          x={12}
          y={6}
        >
          {label}
        </text>
      </g>

      <g name="aircrafts">
        {aircraftTailNumbers.map(tailNumber => {
          const aircraft = aircraftsByTailNumbers[tailNumber];
          const heading = aircraft.Location.Heading;
          const { actualOrgan } = aircraft;
          return (
            <g
              className={classNames(aviationAirportClasses.aircraftWrapper, {
                [aviationAirportClasses[`organ-type-${actualOrgan}`]]:
                  actualOrgan,
              })}
              key={tailNumber}
              transform={`translate(${-AIRCRAFT_SVG_SIZE / 4}, ${-AIRCRAFT_SVG_SIZE / 4})`}
              onClick={() => {
                console.info({
                  id: tailNumber,
                  latitude,
                  longitude,
                  heading,
                  x,
                  y,
                });
              }}
            >
              <AircraftIcon
                style={
                  {
                    ['--heading']: `${(heading ?? 0) - 90}deg`,
                  } as React.CSSProperties
                }
                height={AIRCRAFT_SVG_SIZE / 2}
                width={AIRCRAFT_SVG_SIZE / 2}
                className={aviationAirportClasses.aircraft}
              />
            </g>
          );
        })}
      </g>
      <ZLayer zIndex={EZLayers.Foreground}>
        <AviationAirportAircrafts
          aircraftTailNumbers={aircraftTailNumbers}
          aircraftsByTailNumbers={aircraftsByTailNumbers}
          name={label}
          x={x + 12}
          y={y + 24}
        />
      </ZLayer>
    </Root>
  );
};
